import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import { Clock3, Layers3, MapPin } from "lucide-react";
import type { CalendarEvent, CalendarRecord, CalendarView } from "../types";

type Props = {
  view: CalendarView;
  cursor: Date;
  events: CalendarEvent[];
  calendars: CalendarRecord[];
  timezone: string;
  onEvent: (event: CalendarEvent) => void;
  onEmpty: (date: Date) => void;
};

function formatTime(value: string, timezone: string) {
  return new Intl.DateTimeFormat(undefined, { timeZone: timezone, hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function localDateKey(value: string, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(value));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function eventColor(event: CalendarEvent, calendars: CalendarRecord[]) {
  return calendars.find((calendar) => calendar.id === event.calendarId)?.color ?? "#8b5cf6";
}

function EventChip({ event, color, timezone, onClick, compact = false }: { event: CalendarEvent; color: string; timezone: string; onClick: () => void; compact?: boolean }) {
  return (
    <button className={`event-chip ${compact ? "compact" : ""}`} style={{ "--event-color": color } as React.CSSProperties} onClick={(mouseEvent) => { mouseEvent.stopPropagation(); onClick(); }} title={`${event.title} — ${event.winningReason}`}>
      <span className="event-dot" />
      {!event.allDay ? <time>{formatTime(event.startsAt, timezone)}</time> : null}
      <strong>{event.title}</strong>
      {event.duplicateCount ? <span className="dedupe-badge" title={`${event.duplicateCount} duplicate source record${event.duplicateCount === 1 ? "" : "s"} hidden`}><Layers3 size={11} />{event.duplicateCount + 1}</span> : null}
    </button>
  );
}

export function calendarCursorLabel(view: CalendarView, cursor: Date) {
  if (view === "month") return format(cursor, "MMMM yyyy");
  if (view === "week") {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    const end = endOfWeek(cursor, { weekStartsOn: 1 });
    return isSameMonth(start, end) ? `${format(start, "MMMM d")}–${format(end, "d, yyyy")}` : `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }
  return format(cursor, "MMMM yyyy");
}

export function moveCursor(view: CalendarView, cursor: Date, direction: -1 | 1) {
  if (view === "month" || view === "agenda") return direction === 1 ? addMonths(cursor, 1) : subMonths(cursor, 1);
  return direction === 1 ? addWeeks(cursor, 1) : subWeeks(cursor, 1);
}

function MonthView({ cursor, events, calendars, timezone, onEvent, onEmpty }: Omit<Props, "view">) {
  const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });
  return (
    <div className="month-calendar">
      <div className="weekday-row">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => <span key={day}>{day}</span>)}</div>
      <div className="month-grid">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayEvents = events.filter((event) => localDateKey(event.startsAt, timezone) === key).sort((a, b) => a.startsAt.localeCompare(b.startsAt));
          return (
            <div
              className={`day-cell ${!isSameMonth(day, cursor) ? "outside" : ""}`}
              key={key}
              role="button"
              tabIndex={0}
              onClick={() => onEmpty(day)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onEmpty(day);
              }}
            >
              <span className={`day-number ${isToday(day) ? "today" : ""}`}>{format(day, "d")}</span>
              <div className="day-events">
                {dayEvents.slice(0, 4).map((event) => <EventChip compact key={event.id} event={event} color={eventColor(event, calendars)} timezone={timezone} onClick={() => onEvent(event)} />)}
                {dayEvents.length > 4 ? <span className="more-events">+{dayEvents.length - 4} more</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, events, calendars, timezone, onEvent, onEmpty }: Omit<Props, "view">) {
  const start = startOfWeek(cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, index) => addDays(start, index));
  const hours = Array.from({ length: 15 }, (_, index) => index + 7);
  return (
    <div className="week-calendar">
      <div className="week-header"><span />{days.map((day) => <button key={day.toISOString()} onClick={() => onEmpty(day)} className={isToday(day) ? "today" : ""}><small>{format(day, "EEE")}</small><strong>{format(day, "d")}</strong></button>)}</div>
      <div className="week-scroll">
        <div className="week-hours">{hours.map((hour) => <span key={hour}>{format(new Date(2020, 1, 1, hour), "ha")}</span>)}</div>
        <div className="week-columns">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayEvents = events.filter((event) => localDateKey(event.startsAt, timezone) === key);
            return (
              <div className="week-column" key={key} onDoubleClick={() => onEmpty(day)}>
                {hours.map((hour) => <div className="hour-line" key={hour} />)}
                {dayEvents.map((event) => {
                  const date = new Date(event.startsAt);
                  const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", minute: "numeric", hour12: false }).formatToParts(date);
                  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
                  const hour = Number(values.hour === "24" ? 0 : values.hour);
                  const minute = Number(values.minute);
                  const top = Math.max(0, (hour - 7) * 64 + minute / 60 * 64);
                  const duration = Math.max(32, Math.min(240, (Date.parse(event.endsAt) - Date.parse(event.startsAt)) / 3_600_000 * 64));
                  return (
                    <button className="week-event" key={event.id} style={{ top, height: duration, "--event-color": eventColor(event, calendars) } as React.CSSProperties} onClick={() => onEvent(event)}>
                      <strong>{event.title}</strong><span>{event.allDay ? "All day" : formatTime(event.startsAt, timezone)}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AgendaView({ cursor, events, calendars, timezone, onEvent }: Omit<Props, "view" | "onEmpty">) {
  const monthEvents = events.filter((event) => isSameMonth(new Date(event.startsAt), cursor)).sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const grouped = new Map<string, CalendarEvent[]>();
  for (const event of monthEvents) {
    const key = localDateKey(event.startsAt, timezone);
    grouped.set(key, [...(grouped.get(key) ?? []), event]);
  }
  if (!monthEvents.length) return <div className="empty-state"><div className="empty-orbit"><Clock3 /></div><h3>No events this month</h3><p>There’s room to breathe—or room to plan something new.</p></div>;
  return (
    <div className="agenda-list">
      {[...grouped.entries()].map(([key, dayEvents]) => {
        const date = new Date(`${key}T12:00:00`);
        return <section className="agenda-day" key={key}><div className="agenda-date"><strong>{format(date, "d")}</strong><span>{format(date, "EEE")}<small>{format(date, "MMMM")}</small></span></div><div className="agenda-events">{dayEvents.map((event) => <button className="agenda-event" key={event.id} onClick={() => onEvent(event)} style={{ "--event-color": eventColor(event, calendars) } as React.CSSProperties}><span className="agenda-color"/><div className="agenda-time">{event.allDay ? "All day" : <>{formatTime(event.startsAt, timezone)}<small>{formatTime(event.endsAt, timezone)}</small></>}</div><div className="agenda-copy"><strong>{event.title}</strong>{event.location ? <span><MapPin size={13}/>{event.location}</span> : null}</div>{event.duplicateCount ? <span className="dedupe-pill"><Layers3 size={13}/>Merged {event.duplicateCount + 1}</span> : null}</button>)}</div></section>;
      })}
    </div>
  );
}

export function CalendarBoard(props: Props) {
  if (props.view === "month") return <MonthView {...props} />;
  if (props.view === "week") return <WeekView {...props} />;
  return <AgendaView {...props} />;
}
